import React from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Share2,
  Copy,
  Mail,
  MessageCircle,
  Send,
} from 'lucide-react';

// Import custom icons for social media platforms to avoid deprecation warnings
import { FaFacebook, FaTwitter, FaLinkedin } from 'react-icons/fa';
import { toast } from '@/components/ui/use-toast';

interface SocialShareMenuProps {
  url: string;
  title: string;
  description?: string;
  imageUrl?: string;
  className?: string;
}

const SocialShareMenu: React.FC<SocialShareMenuProps> = ({
  url,
  title,
  description = '',
  imageUrl = '',
  className = '',
}) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);

  // Create sharing links with image where supported
  const shareLinks = {
    // Facebook supports og:image via the URL's Open Graph tags
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,

    // Twitter can include an image parameter
    twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,

    // LinkedIn relies on Open Graph tags in the shared URL
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,

    // Email doesn't support images in the link itself
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,

    // WhatsApp doesn't support images in the link itself
    whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,

    // Telegram doesn't support images in the sharing link
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,

    // Facebook Messenger
    messenger: `https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=291494419107518&redirect_uri=${encodedUrl}`,

    // SMS doesn't support images
    sms: `sms:?body=${encodedTitle}%20${encodedUrl}`,
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(url);

    // Show toast notification
    toast({
      title: "Link Copied! 🔗",
      description: "Training link has been copied to clipboard",
      className: "bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-none shadow-lg",
      duration: 3000,
    });

    // Create a temporary element to show a visual feedback
    const tempElement = document.createElement('div');
    tempElement.className = 'fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/80 text-white px-4 py-2 rounded-lg z-50 animate-in fade-in-0 zoom-in-95 duration-300';
    tempElement.innerText = 'Link Copied!';
    document.body.appendChild(tempElement);

    // Remove the element after animation
    setTimeout(() => {
      tempElement.classList.add('animate-out', 'fade-out-0', 'zoom-out-95');
      setTimeout(() => document.body.removeChild(tempElement), 300);
    }, 1500);
  };

  const handleShare = (platform: string) => {
    // Get the sharing URL
    const shareUrl = shareLinks[platform];

    // Log for debugging
    console.log(`Sharing on ${platform}:`, shareUrl);

    // Show a toast notification indicating which platform is being used
    toast({
      title: `Opening ${platform.charAt(0).toUpperCase() + platform.slice(1)}...`,
      description: "Redirecting to share this training",
      className: "bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-none shadow-lg",
      duration: 2000,
    });

    // Open the sharing link in a new tab
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`transition-all hover:bg-primary/10 group relative overflow-hidden ${className}`}
        >
          <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-purple-500/10 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <Share2 className="mr-2 h-4 w-4 relative z-10 group-hover:animate-pulse" />
          <span className="relative z-10">Share</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 p-2 bg-white border border-muted/30 shadow-lg rounded-lg animate-in fade-in-80 slide-in-from-top-5 duration-200">
        <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Share2 className="h-4 w-4 text-primary" />
          <span>Share via direct link</span>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="flex items-center cursor-pointer hover:bg-primary/5 transition-colors"
          onClick={() => handleShare('facebook')}
        >
          <FaFacebook className="mr-2 h-4 w-4 text-blue-600" />
          <span>Facebook</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex items-center cursor-pointer hover:bg-primary/5 transition-colors"
          onClick={() => handleShare('twitter')}
        >
          <FaTwitter className="mr-2 h-4 w-4 text-sky-500" />
          <span>Twitter</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex items-center cursor-pointer hover:bg-primary/5 transition-colors"
          onClick={() => handleShare('linkedin')}
        >
          <FaLinkedin className="mr-2 h-4 w-4 text-blue-700" />
          <span>LinkedIn</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex items-center cursor-pointer hover:bg-primary/5 transition-colors"
          onClick={() => handleShare('whatsapp')}
        >
          <MessageCircle className="mr-2 h-4 w-4 text-green-500" />
          <span>WhatsApp</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex items-center cursor-pointer hover:bg-primary/5 transition-colors"
          onClick={() => handleShare('telegram')}
        >
          <Send className="mr-2 h-4 w-4 text-blue-400" />
          <span>Telegram</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex items-center cursor-pointer hover:bg-primary/5 transition-colors"
          onClick={() => handleShare('messenger')}
        >
          <MessageCircle className="mr-2 h-4 w-4 text-blue-500" />
          <span>Messenger</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex items-center cursor-pointer hover:bg-primary/5 transition-colors"
          onClick={() => handleShare('sms')}
        >
          <MessageCircle className="mr-2 h-4 w-4 text-gray-500" />
          <span>SMS</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex items-center cursor-pointer hover:bg-primary/5 transition-colors"
          onClick={() => handleShare('email')}
        >
          <Mail className="mr-2 h-4 w-4 text-gray-500" />
          <span>Email</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="flex items-center cursor-pointer hover:bg-primary/5 transition-colors"
          onClick={handleCopyLink}
        >
          <Copy className="mr-2 h-4 w-4 text-gray-500" />
          <span>Copy link</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SocialShareMenu;
